import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Clock, ArrowRight, ArrowUpRight, ArrowDownRight, Link as LinkIcon, CheckCircle2, TrendingUp } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import { MasteryRing } from '../../shared/MasteryRing';
import { MODULES, getModuleIcon } from '../../../content';
import { getWeeklyActivityForLearner, getRecentSessionsForLearner, getWeeklyStats, getAllModuleTrends } from '../../../utils/activityHelpers';
import { getMasteryLabel } from '../../../utils/formatters';
import { type LearnerAccount, type ParentAccount, type AccountsState } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
interface HomePageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
export function HomePage({
  accounts,
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  linkParentToLearnerById,
  onNavigateToLearner,
  onNavigate
}: HomePageProps) {
  // Empty state: No children linked
  if (linkedLearners.length === 0 || !selectedChild || !childData) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-4154">
        <div className="max-w-2xl mx-auto mt-12" data-id="element-4155">
          <div className="flex flex-col items-center mb-8" data-id="element-4156">
            <DjayAvatar size="lg" emotion="happy" animate data-id="element-4157" />
            <div className="mt-6 w-full max-w-sm" data-id="element-4158">
              <DjayBubble message="Hoi! Ik ben Djay. Laten we samen de voortgang van je kind volgen!" position="top" colorScheme="emerald" autoDismissMs={0} data-id="element-4159" />
            </div>
          </div>

          <Card className="p-8 border-emerald-100 shadow-sm text-center bg-white" data-id="element-4160">
            <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-4161">
              Geen kind gekoppeld
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-4162">
              Je hebt nog geen kind gekoppeld aan je account. Ga naar de
              instellingen om een kind te koppelen met een koppelcode.
            </p>

            <div className="flex justify-center" data-id="element-4163">
              <Button onClick={() => onNavigate ? onNavigate('settings') : window.location.hash = 'settings'} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl" data-id="element-4164">
                <LinkIcon className="w-4 h-4 mr-2" data-id="element-4165" />
                Naar Instellingen
              </Button>
            </div>
          </Card>
        </div>
      </PageLayout>;
  }
  // Data processing
  const weeklyActivity = useMemo(() => getWeeklyActivityForLearner(childData.questionStates || {}), [childData.questionStates]);
  const weeklyStats = useMemo(() => getWeeklyStats(childData.questionStates || {}), [childData.questionStates]);
  const recentSessions = useMemo(() => getRecentSessionsForLearner(childData.questionStates || {}, 5), [childData.questionStates]);
  const trends = useMemo(() => getAllModuleTrends(childData.familyMastery || {}, parseInt(selectedGroup) || 6), [childData.familyMastery, selectedGroup]);
  const moduleMastery = useMemo(() => {
    return MODULES.map(module => ({
      module,
      mastery: getLearnerModuleMastery(selectedChild.id, module.id)
    })).filter(m => m.mastery > 0).sort((a, b) => b.mastery - a.mastery);
  }, [selectedChild.id, getLearnerModuleMastery]);
  // Determine Djay's message
  const getDjayMessage = () => {
    if (weeklyStats.practiceDays >= 3) {
      return {
        emotion: 'proud' as const,
        message: `Wat een topweek! ${selectedChild.displayName} heeft al ${weeklyStats.practiceDays} dagen geoefend.`
      };
    }
    if (moduleMastery.length > 0 && moduleMastery[0].mastery > 70) {
      return {
        emotion: 'happy' as const,
        message: `${selectedChild.displayName} is super goed bezig met ${moduleMastery[0].module.title.toLowerCase()}!`
      };
    }
    if (weeklyStats.practiceDays === 0) {
      return {
        emotion: 'encouraging' as const,
        message: `Tijd voor een oefensessie? 15 minuutjes per dag maakt al een groot verschil!`
      };
    }
    return {
      emotion: 'happy' as const,
      message: `Hier is het overzicht van hoe het gaat met ${selectedChild.displayName}.`
    };
  };
  const djayMessage = getDjayMessage();
  const overallMastery = moduleMastery.length > 0 ? Math.round(moduleMastery.reduce((sum, m) => sum + m.mastery, 0) / moduleMastery.length) : 0;
  const strongestModule = moduleMastery[0];
  const weakestModule = moduleMastery[moduleMastery.length - 1];
  return <PageLayout title="Home" description="Overzicht van de voortgang" data-id="element-4166">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4167">
        {/* Child Selector */}
        {linkedLearners.length > 1 && <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-4168">
            {linkedLearners.map(learner => <button key={learner.id} onClick={() => setViewingLearner(learner.id)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${selectedChild.id === learner.id ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`} data-id="element-4169">
                {learner.displayName}
              </button>)}
          </div>}

        {/* Hero Greeting Banner */}
        <Card className="overflow-hidden border-none shadow-md bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 relative" data-id="element-4170">
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} data-id="element-4171"></div>

          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-4172">
            <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0" data-id="element-4173">
              <DjayAvatar size="lg" emotion={djayMessage.emotion} data-id="element-4174" />
            </div>
            <div className="flex-1 text-center sm:text-left pt-2" data-id="element-4175">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2" data-id="element-4176">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-4177">
                  Hoe gaat het met {selectedChild.displayName}?
                </h1>
                <div className="flex items-center justify-center gap-2" data-id="element-4178">
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-4179">
                    <Star className="w-3 h-3 fill-current" data-id="element-4180" /> Lvl{' '}
                    {childData.stats?.level || 1}
                  </span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-4181">
                    <Trophy className="w-3 h-3" data-id="element-4182" /> {childData.stats?.xp || 0} XP
                  </span>
                </div>
              </div>
              <p className="text-emerald-50 text-lg font-medium max-w-2xl leading-relaxed" data-id="element-4183">
                {djayMessage.message}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4184">
          {/* Left Column: Weekly Rhythm & Modules */}
          <div className="lg:col-span-2 space-y-8" data-id="element-4185">
            {/* Weekly Rhythm */}
            <section data-id="element-4186">
              <h2 className="text-xl font-black text-slate-900 mb-4 flex items-center gap-2" data-id="element-4187">
                <Clock className="w-6 h-6 text-emerald-500" data-id="element-4188" />
                Deze week
              </h2>
              <Card className="p-6 border-slate-200 shadow-sm bg-white" data-id="element-4189">
                <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-4190">
                  <div className="flex-1 w-full" data-id="element-4191">
                    <div className="flex justify-between items-center mb-4 relative" data-id="element-4192">
                      {/* Connecting line */}
                      <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-slate-100 -z-10 -translate-y-1/2" data-id="element-4193"></div>

                      {weeklyActivity.map((day, i) => <div key={i} className="flex flex-col items-center gap-2 relative" data-id="element-4194">
                          <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-4195">
                            {day.label}
                          </div>
                          <div className="relative" data-id="element-4196">
                            {day.isToday && !day.practiced && <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-20" data-id="element-4197"></div>}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 relative z-10 ${day.practiced ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200 scale-110' : day.isToday ? 'bg-white border-2 border-emerald-400 text-emerald-600' : 'bg-white border-2 border-slate-100 text-slate-300'}`} data-id="element-4198">
                              {day.practiced ? <span data-id="element-4199">{day.questionCount}</span> : '-'}
                            </div>
                            {day.practiced && <div className="absolute -top-2 -right-2 text-sm animate-bounce" data-id="element-4200">
                                ✨
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <div className="flex gap-6 md:border-l md:border-slate-100 md:pl-8 flex-wrap justify-center" data-id="element-4201">
                    <div className="text-center" data-id="element-4202">
                      <div className="text-3xl font-black text-emerald-600" data-id="element-4203">
                        {weeklyStats.practiceDays}
                      </div>
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-4204">
                        Dagen
                      </div>
                    </div>
                    <div className="text-center" data-id="element-4205">
                      <div className="text-3xl font-black text-teal-600" data-id="element-4206">
                        {weeklyStats.totalQuestions}
                      </div>
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-4207">
                        Vragen
                      </div>
                    </div>
                    <div className="text-center" data-id="element-4208">
                      <div className="text-3xl font-black text-indigo-600" data-id="element-4209">
                        {weeklyStats.accuracy}%
                      </div>
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-4210">
                        Goed
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </section>

            {/* Voortgang */}
            <section data-id="element-4211">
              <h2 className="text-xl font-black text-slate-900 mb-4 flex items-center gap-2" data-id="element-4212">
                <TrendingUp className="w-6 h-6 text-emerald-500" data-id="element-4213" />
                Voortgang
              </h2>
              <Card className="p-6 border-slate-200 shadow-sm bg-white" data-id="element-4214">
                {moduleMastery.length > 0 ? <div className="flex flex-col gap-6" data-id="element-4215">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-4216">
                      <div className="flex-shrink-0 flex flex-col items-center" data-id="element-4217">
                        <MasteryRing value={overallMastery} size={80} strokeWidth={6} color={overallMastery >= 60 ? 'emerald' : overallMastery < 40 ? 'rose' : 'amber'} data-id="element-4218" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-3" data-id="element-4219">
                          Gemiddeld
                        </span>
                      </div>
                      <div className="flex-1 w-full space-y-3" data-id="element-4220">
                        <div className="bg-slate-50 rounded-xl p-3 flex justify-between items-center" data-id="element-4221">
                          <div className="flex items-center gap-3" data-id="element-4222">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-4223">
                              <Trophy className="w-4 h-4" data-id="element-4224" />
                            </div>
                            <span className="text-sm font-bold text-slate-700" data-id="element-4225">
                              Sterkste vak
                            </span>
                          </div>
                          <div className="text-right" data-id="element-4226">
                            <div className="text-sm font-black text-slate-900" data-id="element-4227">
                              {strongestModule.module.title}
                            </div>
                            <div className="text-xs font-bold text-emerald-600" data-id="element-4228">
                              {strongestModule.mastery}% beheersing
                            </div>
                          </div>
                        </div>
                        {moduleMastery.length > 1 && <div className="bg-slate-50 rounded-xl p-3 flex justify-between items-center" data-id="element-4229">
                            <div className="flex items-center gap-3" data-id="element-4230">
                              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-4231">
                                <TrendingUp className="w-4 h-4" data-id="element-4232" />
                              </div>
                              <span className="text-sm font-bold text-slate-700" data-id="element-4233">
                                Grootste groei-kans
                              </span>
                            </div>
                            <div className="text-right" data-id="element-4234">
                              <div className="text-sm font-black text-slate-900" data-id="element-4235">
                                {weakestModule.module.title}
                              </div>
                              <div className="text-xs font-bold text-amber-600" data-id="element-4236">
                                {weakestModule.mastery}% beheersing
                              </div>
                            </div>
                          </div>}
                        <div className="text-sm font-medium text-slate-500 text-center sm:text-left pt-2" data-id="element-4237">
                          {moduleMastery.length} van de 10 vakken geoefend
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => onNavigate && onNavigate('growth')} className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-xl border-none" variant="secondary" data-id="element-4238">
                      Bekijk alle vakken <ArrowRight className="w-4 h-4 ml-2" data-id="element-4239" />
                    </Button>
                  </div> : <div className="p-8 text-center bg-slate-50 rounded-2xl border border-slate-200 border-dashed" data-id="element-4240">
                    <p className="text-slate-500 font-medium" data-id="element-4241">
                      Nog geen vakken geoefend.
                    </p>
                  </div>}
              </Card>
            </section>
          </div>

          {/* Right Column: Profile & Recent */}
          <div className="space-y-8" data-id="element-4242">
            {/* Profile Card */}
            <Card className="overflow-hidden border-slate-200 shadow-sm bg-white" data-id="element-4243">
              <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-6 text-center relative" data-id="element-4244">
                <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '16px 16px'
              }} data-id="element-4245"></div>
                <div className="w-24 h-24 mx-auto bg-white rounded-2xl flex items-center justify-center text-emerald-600 text-4xl font-black shadow-lg transform rotate-3 mb-4 relative z-10" data-id="element-4246">
                  {selectedChild.displayName.charAt(0).toUpperCase()}
                </div>
                <h2 className="text-2xl font-black text-white relative z-10" data-id="element-4247">
                  {selectedChild.displayName}
                </h2>
                <div className="text-sm font-bold text-emerald-100 relative z-10" data-id="element-4248">
                  Groep {selectedGroup}
                </div>
              </div>

              <div className="p-6" data-id="element-4249">
                <div className="flex justify-center gap-4 mb-6" data-id="element-4250">
                  <div className="bg-amber-50 px-4 py-2 rounded-xl flex items-center gap-2 flex-1 justify-center" data-id="element-4251">
                    <Star className="w-5 h-5 text-amber-500 fill-current" data-id="element-4252" />
                    <div className="text-left" data-id="element-4253">
                      <div className="text-[10px] font-bold text-amber-700/70 uppercase tracking-wider" data-id="element-4254">
                        Level
                      </div>
                      <div className="font-black text-amber-700 leading-none" data-id="element-4255">
                        {childData.stats?.level || 1}
                      </div>
                    </div>
                  </div>
                  <div className="bg-violet-50 px-4 py-2 rounded-xl flex items-center gap-2 flex-1 justify-center" data-id="element-4256">
                    <Trophy className="w-5 h-5 text-violet-500" data-id="element-4257" />
                    <div className="text-left" data-id="element-4258">
                      <div className="text-[10px] font-bold text-violet-700/70 uppercase tracking-wider" data-id="element-4259">
                        XP
                      </div>
                      <div className="font-black text-violet-700 leading-none" data-id="element-4260">
                        {childData.stats?.xp || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {onNavigateToLearner && <Button onClick={() => onNavigateToLearner(selectedChild.id)} className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-xl border-none" variant="secondary" data-id="element-4261">
                    Kijk mee met {selectedChild.displayName}{' '}
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-4262" />
                  </Button>}
              </div>
            </Card>

            {/* Recent Sessions */}
            <section data-id="element-4263">
              <h3 className="text-lg font-black text-slate-900 mb-4" data-id="element-4264">
                Laatste sessies
              </h3>
              <Card className="p-5 border-slate-200 shadow-sm bg-white" data-id="element-4265">
                {recentSessions.length > 0 ? <div className="space-y-3" data-id="element-4266">
                    {recentSessions.map((session, i) => {
                  const isGood = session.accuracy >= 70;
                  const isWarning = session.accuracy < 50;
                  const dotColor = isGood ? 'bg-emerald-500' : isWarning ? 'bg-rose-500' : 'bg-amber-500';
                  return <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-4267">
                          <div className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`} data-id="element-4268"></div>
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-4269">
                            {(() => {
                        const mod = MODULES.find(m => m.id === session.moduleId);
                        const IconComp = getModuleIcon(mod?.icon || 'BookOpen');
                        return <IconComp className="w-5 h-5 text-slate-500" data-id="element-4270" />;
                      })()}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4271">
                            <div className="font-bold text-slate-900 truncate" data-id="element-4272">
                              {session.moduleTitle}
                            </div>
                            <div className="text-xs font-medium text-slate-500" data-id="element-4273">
                              {new Date(session.date).toLocaleDateString('nl-NL', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                            </div>
                          </div>
                          <div className="text-right" data-id="element-4274">
                            <div className="font-black text-slate-900" data-id="element-4275">
                              {session.accuracy}%
                            </div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-4276">
                              Goed
                            </div>
                          </div>
                        </div>;
                })}
                  </div> : <div className="text-center py-6 text-slate-500 font-medium" data-id="element-4277">
                    Nog geen sessies.
                  </div>}
              </Card>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>;
}